/**
 * Rendering / responsiveness measurement.
 *
 * For "this machine draws the whole page slowly". The browser gives four
 * independent views of that, and they point at different culprits — which is
 * the reason to collect all four rather than one:
 *
 *   FPS            — how often a frame actually reaches the screen.
 *   Long tasks     — single blocks of JS that hold the thread past 50 ms.
 *   Input delay    — how long a click waited before anything ran (Event Timing).
 *   DOM / heap     — how much there is to lay out, and whether memory is
 *                    climbing toward GC pressure.
 *
 * A low FPS with no long tasks is the compositor or the GPU, not JavaScript.
 * A low FPS with long tasks is JavaScript. High input delay with a healthy FPS
 * means the thread is busy in bursts between frames. Reading one number alone
 * cannot tell those apart.
 *
 * Measurement is cheap but not free (a rAF loop and two observers), so it runs
 * only while the panel is open.
 */
/** Rolling window. Long enough to smooth a stutter, short enough to feel live. */
const WINDOW_MS = 5000;
let _running = false;
let _raf = 0;
let _lastFrameAt = 0;
/** Frame intervals in the window. */
let _frames = [];
/** Long tasks (≥50 ms) in the window. */
let _tasks = [];
/** Input delays (Event Timing) in the window. */
let _inputs = [];
/**
 * Cached DOM/video counts, and when they were taken.
 *
 * Walking the whole document is O(nodes) and this panel exists precisely for
 * machines where the document is already too expensive to lay out — counting it
 * twice a second would add to the very load we are trying to measure. The
 * counts move slowly, so a stale one costs nothing.
 */
const COUNT_INTERVAL_MS = 2000;
let _countAt = Number.NEGATIVE_INFINITY;
let _domNodes = 0;
let _videos = 0;
let _taskObserver = null;
let _eventObserver = null;
let _longTaskSupported = false;
let _eventTimingSupported = false;
/**
 * Drops samples that have fallen out of the rolling window.
 *
 * @param {number} now - Current timestamp.
 * @returns {void}
 */
function _prune(now) {
    const cutoff = now - WINDOW_MS;
    _frames = _frames.filter(f => f.at >= cutoff);
    _tasks = _tasks.filter(t => t.at >= cutoff);
    _inputs = _inputs.filter(i => i.at >= cutoff);
}
/**
 * Starts measuring. Safe to call repeatedly.
 *
 * @returns {void}
 */
export function startRenderMonitor() {
    if (_running || typeof window === 'undefined') {
        return;
    }
    _running = true;
    _frames = [];
    _tasks = [];
    _inputs = [];
    _lastFrameAt = 0;
    // Negative infinity, not 0: right after a page load performance.now() is
    // itself smaller than the interval, so a zero would suppress the very first
    // count and leave the panel reading 0 elements for two seconds.
    _countAt = Number.NEGATIVE_INFINITY;
    // Frame pacing. The interval between callbacks IS the frame time the user
    // sees — a number no counter inside the app can substitute for.
    const frame = () => {
        if (!_running) {
            return;
        }
        const now = performance.now();
        if (_lastFrameAt) {
            _frames.push({ at: now, ms: now - _lastFrameAt });
        }
        _lastFrameAt = now;
        _prune(now);
        _raf = requestAnimationFrame(frame);
    };
    _raf = requestAnimationFrame(frame);
    try {
        _taskObserver = new PerformanceObserver(list => {
            for (const e of list.getEntries()) {
                _tasks.push({ at: performance.now(), ms: e.duration });
            }
        });
        _taskObserver.observe({ entryTypes: ['longtask'] });
        _longTaskSupported = true;
    }
    catch {
        _taskObserver = null;
        _longTaskSupported = false;
    }
    try {
        _eventObserver = new PerformanceObserver(list => {
            for (const e of list.getEntries()) {
                // processingStart - startTime is the wait before the handler
                // ran: the part of "the UI feels stuck" the user actually feels.
                const ev = e;
                const delay = ev.processingStart - ev.startTime;
                if (delay >= 0) {
                    _inputs.push({ at: performance.now(), ms: delay });
                }
            }
        });
        // durationThreshold 16 keeps the noise of every trivial event out.
        _eventObserver.observe({ type: 'event', buffered: true, durationThreshold: 16 });
        _eventTimingSupported = true;
    }
    catch {
        _eventObserver = null;
        _eventTimingSupported = false;
    }
}
/**
 * Stops measuring and clears the window.
 *
 * @returns {void}
 */
export function stopRenderMonitor() {
    _running = false;
    cancelAnimationFrame(_raf);
    for (const o of [_taskObserver, _eventObserver]) {
        if (o) {
            try {
                o.disconnect();
            }
            catch { /* ignore */ }
        }
    }
    _taskObserver = null;
    _eventObserver = null;
    _frames = [];
    _tasks = [];
    _inputs = [];
}
/**
 * Returns the figures for the rolling window.
 *
 * @returns {IRenderStats}
 */
export function getRenderStats() {
    const now = performance.now();
    _prune(now);
    const fCount = _frames.length;
    const fSum = _frames.reduce((a, f) => a + f.ms, 0);
    const fMax = _frames.reduce((a, f) => (f.ms > a ? f.ms : a), 0);
    // 33 ms ≈ two 60 Hz frames: past that a dropped frame is visible.
    const jank = _frames.filter(f => f.ms > 33).length;
    if (typeof document !== 'undefined' && now - _countAt >= COUNT_INTERVAL_MS) {
        _countAt = now;
        _domNodes = document.getElementsByTagName('*').length;
        _videos = document.getElementsByTagName('video').length;
    }
    const heap = performance.memory;
    return {
        fps: fSum > 0 ? fCount / (fSum / 1000) : 0,
        frameAvgMs: fCount ? fSum / fCount : 0,
        frameMaxMs: fMax,
        jankFrames: jank,
        longTasks: _tasks.length,
        longTaskMs: _tasks.reduce((a, t) => a + t.ms, 0),
        longTaskMaxMs: _tasks.reduce((a, t) => (t.ms > a ? t.ms : a), 0),
        longTaskSupported: _longTaskSupported,
        inputAvgMs: _inputs.length ? _inputs.reduce((a, i) => a + i.ms, 0) / _inputs.length : 0,
        inputMaxMs: _inputs.reduce((a, i) => (i.ms > a ? i.ms : a), 0),
        inputCount: _inputs.length,
        eventTimingSupported: _eventTimingSupported,
        domNodes: _domNodes,
        videos: _videos,
        heapMB: heap ? heap.usedJSHeapSize / 1048576 : null,
        heapLimitMB: heap ? heap.jsHeapSizeLimit / 1048576 : null
    };
}
/**
 * Whether the monitor is running.
 *
 * @returns {boolean}
 */
export function isRenderMonitorRunning() {
    return _running;
}
